import { LIST_MARKER_CSS, textBoxStyle, textContentStyle } from "@cva/renderer";
import { buildParentMap, removeElements, type TextElement, worldBox } from "@cva/shared";
import { EditorState, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { type CSSProperties, useEffect, useLayoutEffect, useRef } from "react";
import {
  clipboardText,
  createRichState,
  docToValue,
  elementToDoc,
  initialSelection,
  type RichContext,
  sameValue,
  selectionFormat,
  useRichText,
} from "./richtext";
import { useEditor, useEditorApi } from "./store";

const PLACEHOLDERS = new Set(["Überschrift", "Unterüberschrift", "Dein Text hier", "Text"]);

/**
 * Clicks and focus inside these keep the text in edit mode: the properties
 * panel formats the selection, its popovers (colour, font) are portalled.
 */
const KEEP_EDITING = "[data-text-format-scope], [data-radix-popper-content-wrapper]";

function keepsEditing(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(KEEP_EDITING) !== null;
}

/**
 * Inline text editing: a ProseMirror editor with exactly the renderer's
 * styles, placed over the (hidden) text element (richtext.ts). The whole edit
 * is one undo step of the document.
 */
export function TextEditor({ element }: { element: TextElement }) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const mountRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const elementRef = useRef(element);
  elementRef.current = element;
  const box = worldBox(doc, element.id, buildParentMap(doc)) ?? {
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    rotation: element.rotation,
  };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const elementId = element.id;
    // Long-lived: changes of others show meanwhile.
    store.getState().beginGesture({ live: true });

    const finish = () => store.getState().setEditingText(null);
    /** Changes of the element itself, as part of this edit's undo step. */
    const update = (recipe: (target: TextElement) => void) => {
      const state = store.getState();
      // A colour popover closing ends the gesture; keep the edit together.
      if (!state.gestureBase) state.beginGesture({ live: true });
      state.update((draft) => {
        const target = draft.elements[elementId];
        if (target?.type === "text") recipe(target as TextElement);
      });
    };
    const context = (): RichContext => ({
      element: elementRef.current,
      patchElement: update,
    });

    const initial = elementRef.current;
    let state = createRichState(initial, { context, finish });
    state = state.apply(initialSelection(state, PLACEHOLDERS.has(initial.text)));

    const publish = (current: EditorState) =>
      useRichText.getState().set({ format: selectionFormat(current, elementRef.current) });

    const view = new EditorView(
      { mount },
      {
        state,
        attributes: {
          role: "textbox",
          "aria-multiline": "true",
          "aria-label": "Text bearbeiten",
          spellcheck: "true",
          lang: "de",
        },
        clipboardTextSerializer: clipboardText,
        dispatchTransaction(tr) {
          const next = view.state.apply(tr);
          view.updateState(next);
          if (tr.docChanged) {
            const value = docToValue(next.doc);
            update((target) => {
              target.text = value.text;
              if (value.marks) target.marks = value.marks;
              else delete target.marks;
              if (value.paragraphs) target.paragraphs = value.paragraphs;
              else delete target.paragraphs;
            });
          }
          publish(next);
        },
        handleDOMEvents: {
          blur: (_view, event) => {
            const next = (event as FocusEvent).relatedTarget;
            // Focus moving elsewhere by keyboard ends editing; clicks are handled below.
            if (next && !mount.contains(next as Node) && !keepsEditing(next)) finish();
            return false;
          },
          keydown: (_view, event) => {
            // The editor's global shortcuts must not see keys typed into the text.
            event.stopPropagation();
            return false;
          },
        },
      },
    );
    viewRef.current = view;
    useRichText.getState().set({ elementId, view, context });
    publish(view.state);
    view.focus();

    // A click anywhere but the text, the properties panel or its popovers ends editing.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && (mount.contains(target) || keepsEditing(target))) return;
      finish();
    };
    document.addEventListener("pointerdown", onPointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      if (useRichText.getState().elementId === elementId) {
        useRichText.getState().set({ elementId: null, view: null, format: null, context: null });
      }
      view.destroy();
      viewRef.current = null;
      const current = store.getState();
      current.endGesture();
      // Remove text boxes that were left empty.
      const left = current.doc.elements[elementId];
      if (left?.type === "text" && left.text.trim() === "") {
        current.update((draft) => removeElements(draft, [elementId]));
      }
    };
  }, [element.id, store]);

  // Changes from outside the editor (whole-text formatting, others' edits):
  // take them in, keeping the selection where it was.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (sameValue(docToValue(view.state.doc), element)) {
      useRichText.getState().set({ format: selectionFormat(view.state, element) });
      return;
    }
    const { from, to } = view.state.selection;
    const nextDoc = elementToDoc(element);
    const size = nextDoc.content.size;
    const clamp = (pos: number) => Math.max(1, Math.min(size - 1, pos));
    let next = EditorState.create({ doc: nextDoc, plugins: view.state.plugins });
    next = next.apply(next.tr.setSelection(TextSelection.create(nextDoc, clamp(from), clamp(to))));
    view.updateState(next);
    useRichText.getState().set({ format: selectionFormat(next, element) });
  }, [element]);

  // Auto height while typing
  useLayoutEffect(() => {
    const node = mountRef.current;
    if (!node || !element.autoHeight) return;
    const height = Math.max(1, Math.ceil(node.offsetHeight));
    if (Math.abs(height - element.height) > 0.5) {
      store.getState().update((draft) => {
        const target = draft.elements[element.id];
        if (target?.type !== "text") return;
        const delta = height - target.height;
        const angle = (target.rotation * Math.PI) / 180;
        target.x += (-Math.sin(angle) * delta) / 2;
        target.y += (Math.cos(angle) * delta) / 2 - delta / 2;
        target.height = height;
      });
    }
  });

  const style = textBoxStyle({ ...element, ...box });
  const content: CSSProperties = {
    ...textContentStyle(element),
    outline: "none",
    cursor: "text",
    caretColor: element.style.color.length <= 7 ? element.style.color : undefined,
    userSelect: "text",
    WebkitUserSelect: "text",
    ...(element.style.letterSpacing
      ? ({ "--text-letter-spacing": `${element.style.letterSpacing}em` } as CSSProperties)
      : {}),
  };
  return (
    <div
      style={{ ...style, opacity: 1, zIndex: 5 }}
      onPointerDown={(event) => event.stopPropagation()}
      onCopy={(event) => event.stopPropagation()}
      onCut={(event) => event.stopPropagation()}
      onPaste={(event) => event.stopPropagation()}
    >
      <style>{LIST_MARKER_CSS}</style>
      <div ref={mountRef} style={content} />
    </div>
  );
}
